import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { doc, getDoc, DocumentReference, setDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext'; // 确保已引入
import { db } from '../firebase';
import { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import SwiperCore from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

SwiperCore.use([Navigation, Pagination]);

type Variant = {
  colorCode: string;
  colorId: string;
  image: string;
  pickUpNum: number;
  reservedNum: number;
  stock: number;
};

type Product = {
  id: string;
  title: string;
  price: number;
  images: string[];
  description: string;
  category?: DocumentReference;
  availableNum?: string;
  variants?: Variant[];
  inspirationImages?: string[];
};


const CART_LOCAL_KEY = 'cart';

const ProductDetail: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  // 尝试从 location.state 获取 categoryId 和 categoryName
  const state = location.state as { categoryId?: string; categoryName?: string } | undefined;
  const categoryId = state?.categoryId;
  const categoryName = state?.categoryName;

  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [modalImg, setModalImg] = useState<string | null>(null);
  const [localCategoryId, setLocalCategoryId] = useState<string | undefined>(categoryId);
  const [localCategoryName, setLocalCategoryName] = useState<string | undefined>(categoryName);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState<number>(0);
  const [colorMap, setColorMap] = useState<{ [colorId: string]: string }>({});
  const [addSuccess, setAddSuccess] = useState(false);
  const variantImages = product && product.variants ? product.variants.map(v => v.image) : [];
  const allImages = [...variantImages, ...(product && product.images ? product.images : [])];

  // 用于 hover 效果的本地 state
  const [homeHover, setHomeHover] = useState(false);
  const [catHover, setCatHover] = useState(false);
  const swiperRef = useRef<SwiperClass | null>(null);

  // 缓存当前选中变体和库存计算
  const currentVariant = useMemo(() => {
    return product?.variants && product.variants.length > 0
      ? product.variants[selectedVariantIdx]
      : null;
  }, [product, selectedVariantIdx]);

  const availableStock = useMemo(() => {
    return currentVariant
      ? currentVariant.stock
      : Number(product?.availableNum) || 0;
  }, [currentVariant, product]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      // 1. 先从 localStorage 读取
      const cached = localStorage.getItem(`product_${id}`);
      const cacheTime = localStorage.getItem(`product_${id}_time`);
      const now = Date.now();
      const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
      const maxAge = 5 * 60 * 1000; // 5分钟缓存

      if (cached && cacheAge < maxAge) {
        try {
          const productData = JSON.parse(cached);
          setProduct(productData);
          return;
        } catch { }
      }

      // 2. 再请求 Firestore
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const productData = { id: docSnap.id, ...docSnap.data() } as Product;
        setProduct(productData);

        // 3. 缓存产品数据
        localStorage.setItem(`product_${id}`, JSON.stringify(productData));
        localStorage.setItem(`product_${id}_time`, Date.now().toString());
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    // 如果没有categoryId，尝试根据product.category（数组）查一次
    if (!localCategoryId && product?.category && Array.isArray(product.category) && product.category.length > 0) {
      // 取第一个分类id作为主分类
      const firstCatId = product.category[0];
      setLocalCategoryId(firstCatId);

      // 获取分类名称
      const fetchCategory = async () => {
        // 1. 先从 localStorage 读取
        const cached = localStorage.getItem(`category_${firstCatId}`);
        const cacheTime = localStorage.getItem(`category_${firstCatId}_time`);
        const now = Date.now();
        const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
        const maxAge = 10 * 60 * 1000; // 10分钟缓存

        if (cached && cacheAge < maxAge) {
          try {
            const categoryData = JSON.parse(cached);
            setLocalCategoryName(categoryData.name);
            return;
          } catch { }
        }

        // 2. 再请求 Firestore
        const catRef = doc(db, "categories", firstCatId);
        const catSnap = await getDoc(catRef);
        if (catSnap.exists()) {
          const categoryData = catSnap.data();
          setLocalCategoryName(categoryData.name);

          // 3. 缓存分类数据
          localStorage.setItem(`category_${firstCatId}`, JSON.stringify(categoryData));
          localStorage.setItem(`category_${firstCatId}_time`, Date.now().toString());
        }
      };
      fetchCategory();
    }
  }, [product, localCategoryId]);

  useEffect(() => {
    const fetchColors = async () => {
      if (!product?.variants) return;
      const colorIds = Array.from(new Set(product.variants.map(v => v.colorId)));
      const colorMapTemp: { [colorId: string]: string } = {};

      // 1. 先从 localStorage 读取已缓存的颜色
      const uncachedColorIds: string[] = [];
      for (const colorId of colorIds) {
        const cached = localStorage.getItem(`color_${colorId}`);
        const cacheTime = localStorage.getItem(`color_${colorId}_time`);
        const now = Date.now();
        const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
        const maxAge = 10 * 60 * 1000; // 10分钟缓存

        if (cached && cacheAge < maxAge) {
          try {
            const colorData = JSON.parse(cached);
            colorMapTemp[colorId] = colorData.name;
          } catch {
            uncachedColorIds.push(colorId);
          }
        } else {
          uncachedColorIds.push(colorId);
        }
      }

      // 2. 只请求未缓存的颜色
      for (const colorId of uncachedColorIds) {
        const colorDoc = await getDoc(doc(db, 'colors', colorId));
        if (colorDoc.exists()) {
          const colorData = colorDoc.data();
          colorMapTemp[colorId] = colorData.name;

          // 3. 缓存颜色数据
          localStorage.setItem(`color_${colorId}`, JSON.stringify(colorData));
          localStorage.setItem(`color_${colorId}_time`, Date.now().toString());
        }
      }
      setColorMap(colorMapTemp);
    };
    fetchColors();
  }, [product]);

  const handleAddToCart = async () => {
    if (!product) return;

    // 1. 获取当前购物车中该商品该变体的数量
    let variantId = product.variants && product.variants.length > 0
      ? product.variants[selectedVariantIdx].colorId
      : undefined;

    if (user) {
      // 1. 先从 localStorage 读取
      const cached = localStorage.getItem(`cart_${user.uid}`);
      const cacheTime = localStorage.getItem(`cart_${user.uid}_time`);
      const now = Date.now();
      const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
      const maxAge = 1 * 60 * 1000; // 1分钟缓存

      let items: { productId: string; quantity: number; variantId?: string }[] = [];

      if (cached && cacheAge < maxAge) {
        try {
          const cartData = JSON.parse(cached);
          items = cartData.items || [];
        } catch { }
      }

      // 2. 再请求 Firestore
      const cartRef = doc(db, "carts", user.uid);
      const cartDoc = await getDoc(cartRef);
      if (cartDoc.exists()) {
        items = cartDoc.data().items || [];

        // 3. 缓存购物车数据
        localStorage.setItem(`cart_${user.uid}`, JSON.stringify({ items }));
        localStorage.setItem(`cart_${user.uid}_time`, Date.now().toString());
      }
      // 查找同一商品同一变体
      const found = items.find(i =>
        i.productId === product.id &&
        (variantId ? i.variantId === variantId : !i.variantId)
      );

      const currentCartQuantity = found ? found.quantity : 0;
      const newTotalQuantity = currentCartQuantity + quantity;

      // 2. 检查总数量是否超过库存
      if (newTotalQuantity > availableStock) {
        alert(`Sorry! Only ${availableStock} items available in stock. You already have ${currentCartQuantity} in your cart.`);
        return;
      }

      if (found) {
        found.quantity += quantity;
      } else {
        items.push({ productId: product.id, quantity, ...(variantId ? { variantId } : {}) });
      }
      await setDoc(cartRef, { items });
      window.dispatchEvent(new Event('cart-updated'));
    } else {
      // 本地购物车
      let items: { productId: string; quantity: number; variantId?: string }[] = [];
      const localCart = localStorage.getItem(CART_LOCAL_KEY);
      if (localCart) {
        items = JSON.parse(localCart);
      }
      const found = items.find(i =>
        i.productId === product.id &&
        (variantId ? i.variantId === variantId : !i.variantId)
      );

      const currentCartQuantity = found ? found.quantity : 0;
      const newTotalQuantity = currentCartQuantity + quantity;

      // 检查总数量是否超过库存
      if (newTotalQuantity > availableStock) {
        alert(`Sorry! Only ${availableStock} items available in stock. You already have ${currentCartQuantity} in your cart.`);
        return;
      }

      if (found) {
        found.quantity += quantity;
      } else {
        items.push({ productId: product.id, quantity, ...(variantId ? { variantId } : {}) });
      }
      localStorage.setItem(CART_LOCAL_KEY, JSON.stringify(items));
      window.dispatchEvent(new Event('cart-updated'));
    }
    setAddSuccess(true);
    setTimeout(() => setAddSuccess(false), 2000); // 2秒后自动消失
  };

  if (!product) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24, marginBottom: 100, marginTop: 50 }}>
      {/* Breadcrumb */}
      <nav
        style={{
          marginBottom: 38,
          marginTop: 0,
          fontSize: 15,
          color: '#222',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          position: 'relative'
        }}
      >
        {/* Back 按钮靠左 */}
        <button
          onClick={() => {
            if (window.history.length > 1) {
              window.history.back();
            } else if (localCategoryId) {
              window.location.href = `/category/${localCategoryId}`;
            } else {
              window.location.href = '/';
            }
          }}
          style={{
            background: '#f5f5f5',
            color: '#222',
            border: '1px solid #ccc',
            borderRadius: 6,
            padding: '4px 14px',
            cursor: 'pointer',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)'
          }}
        >
          Back
        </button>
        {/* Breadcrumb 居中 */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Link
            to="/"
            style={{
              color: homeHover ? '#27ae60' : '#888',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
            onMouseEnter={() => setHomeHover(true)}
            onMouseLeave={() => setHomeHover(false)}
          >
            Home
          </Link>
          {localCategoryId && (
            <>
              {' '} &gt;{' '}
              <Link
                to={`/category/${localCategoryId}`}
                style={{
                  color: catHover ? '#27ae60' : '#222',
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={() => setCatHover(true)}
                onMouseLeave={() => setCatHover(false)}
              >
                {localCategoryName || 'Category'}
              </Link>
            </>
          )}
          {' '} &gt; <span style={{ color: '#222' }}>{product.title}</span>
        </div>
      </nav>
      {/* <button
        onClick={() => localCategoryId ? navigate(`/category/${localCategoryId}`) : navigate(-1)}
        style={{
          marginBottom: 24,
          background: '#f5f5f5',
          color: '#222',
          border: '1px solid #ccc',
          borderRadius: 6,
          padding: '6px 18px',
          cursor: 'pointer'
        }}
      >
        Back
      </button> */}

      <div style={{ display: 'flex', gap: 60, flexWrap: 'wrap' }}>
        {/* 图片走马灯 */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <Swiper
            navigation
            pagination={{ clickable: true }}
            style={{ borderRadius: 12 }}
            initialSlide={selectedVariantIdx}
            onSwiper={(swiper: SwiperClass) => (swiperRef.current = swiper)}
          >
            {allImages.map((img, idx) => (
              <SwiperSlide key={idx}>
                <img
                  src={img}
                  alt={product.title}
                  style={{ width: '100%', borderRadius: 12, cursor: 'zoom-in', objectFit: 'cover', maxHeight: 400 }}
                  onClick={() => setModalImg(img)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {/* 商品信息 */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <h1 style={{ fontWeight: 700 }}>{product.title}</h1>
          <p style={{ color: '#27ae60', fontWeight: 600, fontSize: 22 }}>
            ${product.price}
            {localCategoryName !== 'Used Furniture' ? '/month' : ''}
          </p>
          {/* 只要不是 Used Furniture 才显示库存 */}
          {localCategoryName !== 'Used Furniture' && (
            product.variants && product.variants.length > 0 ? (
              product.variants[selectedVariantIdx].stock === 0 ? (
                <p style={{ color: '#e74c3c', fontSize: 15, marginBottom: 12 }}>
                  Out of Stock
                </p>
              ) : (
                <p style={{ color: '#27ae60', fontSize: 15, marginBottom: 12 }}>
                  {product.variants[selectedVariantIdx].stock} Available
                </p>
              )
            ) : (
              Number(product.availableNum) === 0 ? (
                <p style={{ color: '#e74c3c', fontSize: 15, marginBottom: 12 }}>
                  Out of Stock
                </p>
              ) : (
                <p style={{ color: '#27ae60', fontSize: 15, marginBottom: 12 }}>
                  {product.availableNum ?? ''} Available
                </p>
              )
            )
          )}
          <p style={{ margin: '18px 0', color: '#444', whiteSpace: 'pre-line' }}>{product.description}</p>

          {/* 颜色变体选择 */}
          {product.variants && product.variants.length > 0 && (
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center', // 居中
              margin: '16px 0'
            }}>
              {product.variants.map((variant, idx) => (
                <div
                  key={variant.colorId}
                  onClick={() => {
                    setSelectedVariantIdx(idx);
                    if (swiperRef.current) {
                      swiperRef.current.slideTo(idx);
                    }
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: variant.colorCode,
                    border: idx === selectedVariantIdx ? '3px solid #27ae60' : '1px solid #ccc',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                  title={colorMap[variant.colorId] || variant.colorCode}
                />
              ))}
            </div>
          )}

          {localCategoryName !== 'Used Furniture' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0', justifyContent: 'center', marginBottom: 40 }}>
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                disabled={quantity <= 1}
                style={{
                  fontSize: 20,
                  cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                  opacity: quantity <= 1 ? 0.5 : 1
                }}
              >-</button>
              <span style={{ minWidth: 32, textAlign: 'center' }}>{quantity}</span>
              <button
                onClick={() => {
                  if (quantity < availableStock) {
                    setQuantity(q => q + 1);
                  } else {
                    alert(`Only ${availableStock} items available in stock.`);
                  }
                }}
                disabled={quantity >= availableStock}
                style={{
                  fontSize: 20,
                  cursor: quantity >= availableStock ? 'not-allowed' : 'pointer',
                  opacity: quantity >= availableStock ? 0.5 : 1
                }}
              >+</button>
            </div>
          )}
          {localCategoryName !== 'Used Furniture' && addSuccess && (
            <div style={{ color: '#27ae60', fontWeight: 600, marginBottom: 6, marginTop: -20, textAlign: 'center' }}>
              Added to cart!
            </div>
          )}
          {localCategoryName !== 'Used Furniture' && (
            <button
              onClick={handleAddToCart}
              style={{
                background: '#27ae60',
                color: '#fff',
                padding: '10px 32px',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 18,
                cursor:
                  (
                    (product.variants && product.variants.length > 0 && product.variants[selectedVariantIdx].stock > 0)
                    ||
                    ((!product.variants || product.variants.length === 0) && Number(product.availableNum) > 0)
                  )
                    ? 'pointer'
                    : 'not-allowed',
                opacity:
                  (
                    (product.variants && product.variants.length > 0 && product.variants[selectedVariantIdx].stock > 0)
                    ||
                    ((!product.variants || product.variants.length === 0) && Number(product.availableNum) > 0)
                  )
                    ? 1
                    : 0.5
              }}
              disabled={
                (product.variants && product.variants.length > 0)
                  ? product.variants[selectedVariantIdx].stock === 0
                  : Number(product.availableNum) === 0
              }
            >
              Add to Cart
            </button>
          )}
        </div>
      </div>

      {/* Ideas & Inspirations Section */}
      {product.inspirationImages && product.inspirationImages.length > 0 && (
        <section style={{ marginTop: 80, marginBottom: 40 }}>
          <div style={{
            textAlign: 'left',
            marginBottom: 32,
            position: 'relative'
          }}>
            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: 700,
              color: '#222',
              margin: 0,
              position: 'relative',
              display: 'inline-block'
            }}>
              Ideas & Inspiration
            </h2>
            <div style={{
              content: '',
              display: 'block',
              width: '60px',
              height: '3px',
              background: '#27ae60',
              marginTop: '8px',
              borderRadius: '2px'
            }}></div>
          </div>

          <div style={{
            display: 'flex',
            gap: 16,
            height: 300,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            {/* 左侧大图 */}
            <div style={{
              flex: '0 0 50%',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <img
                src={product.inspirationImages[0]}
                alt="Inspiration 1"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onClick={() => setModalImg(product.inspirationImages![0])}
              />
            </div>

            {/* 右侧小图网格 */}
            <div style={{
              flex: '0 0 50%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              gap: 8
            }}>
              {product.inspirationImages.slice(1, 5).map((img, index) => (
                <div
                  key={index}
                  style={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 8
                  }}
                >
                  <img
                    src={img}
                    alt={`Inspiration ${index + 2}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onClick={() => setModalImg(img)}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 图片放大 Modal */}
      {modalImg && (
        <div
          onClick={() => setModalImg(null)}
          style={{
            position: 'fixed',
            left: 0, top: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img
            src={modalImg}
            alt="Zoom"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              borderRadius: 12,
              boxShadow: '0 4px 32px rgba(0,0,0,0.25)',
              background: '#fff'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetail;